import { HEADER_MOBILE_BREAKPOINT } from '../config/breakpoints';

// iOS Safari ignores `overflow: hidden` on <body> for touch scrolling. The
// theme already toggles `body.overflow-hidden` when modals (quick search,
// mini cart, etc.) open/close. This pins the page with inline styles while
// that class is present and restores the scroll offset on release.
const iosScrollLockFix = () => {
  const lockClass = 'overflow-hidden';
  const mobileQuery = window.matchMedia(`(max-width: ${HEADER_MOBILE_BREAKPOINT}px)`);
  const bodyStyle = document.body.style;
  const rootStyle = document.documentElement.style;
  const scrollableSelector = [
    '.predictive-search',
    '.search-modal__content',
    '#CartDrawer',
    '.drawer__inner',
    '.mrMiniCart-items',
    'modal-dialog[open] [role="dialog"]',
  ].join(',');

  let scrollY = 0;
  let isPinned = false;
  let touchY = 0;
  let previousBodyStyles = {};
  let previousRootStyles = {};

  const preserveStyles = () => {
    previousBodyStyles = {
      position: bodyStyle.position,
      top: bodyStyle.top,
      left: bodyStyle.left,
      right: bodyStyle.right,
      width: bodyStyle.width,
      overflow: bodyStyle.overflow,
      touchAction: bodyStyle.touchAction,
    };
    previousRootStyles = {
      overflow: rootStyle.overflow,
      overscrollBehavior: rootStyle.overscrollBehavior,
      touchAction: rootStyle.touchAction,
    };
  };

  const restoreStyles = () => {
    Object.keys(previousBodyStyles).forEach((property) => {
      bodyStyle[property] = previousBodyStyles[property];
    });
    Object.keys(previousRootStyles).forEach((property) => {
      rootStyle[property] = previousRootStyles[property];
    });
  };

  const getScrollableParent = (target) => {
    const scrollable = target.closest(scrollableSelector);

    if (!scrollable) return null;
    return scrollable.scrollHeight > scrollable.clientHeight ? scrollable : null;
  };

  const onTouchStart = (event) => {
    touchY = event.touches[0].clientY;
  };

  const onTouchMove = (event) => {
    if (!isPinned) return;

    const target = event.target;
    const scrollable = target instanceof Element ? getScrollableParent(target) : null;

    if (!scrollable) {
      event.preventDefault();
      return;
    }

    const currentY = event.touches[0].clientY;
    const isScrollingDown = currentY > touchY;
    touchY = currentY;

    const isAtTop = scrollable.scrollTop <= 0;
    const isAtBottom = scrollable.scrollTop + scrollable.clientHeight >= scrollable.scrollHeight - 1;

    if ((isAtTop && isScrollingDown) || (isAtBottom && !isScrollingDown)) {
      event.preventDefault();
    }
  };

  const lock = () => {
    if (isPinned) return;

    scrollY = window.scrollY || document.documentElement.scrollTop || 0;
    preserveStyles();

    bodyStyle.position = 'fixed';
    bodyStyle.top = `-${scrollY}px`;
    bodyStyle.left = '0';
    bodyStyle.right = '0';
    bodyStyle.width = '100%';
    bodyStyle.overflow = 'hidden';
    bodyStyle.touchAction = 'none';
    rootStyle.overflow = 'hidden';
    rootStyle.overscrollBehavior = 'none';
    rootStyle.touchAction = 'none';

    document.addEventListener('touchstart', onTouchStart, { passive: true });
    document.addEventListener('touchmove', onTouchMove, { passive: false });

    isPinned = true;
  };

  const unlock = () => {
    if (!isPinned) return;

    document.removeEventListener('touchstart', onTouchStart);
    document.removeEventListener('touchmove', onTouchMove);

    restoreStyles();
    window.scrollTo(0, scrollY);
    isPinned = false;
  };

  const syncLockState = () => {
    const shouldLock = document.body.classList.contains(lockClass) && mobileQuery.matches;

    if (shouldLock) {
      lock();
    } else {
      unlock();
    }
  };

  const observer = new MutationObserver(syncLockState);

  observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
  if (mobileQuery.addEventListener) {
    mobileQuery.addEventListener('change', syncLockState);
  } else {
    mobileQuery.addListener(syncLockState);
  }

  syncLockState();
};

export default iosScrollLockFix;
